// ============================================================================
// MARKET DATA ENGINE — Real data only. No simulation.
//
// US equities only (NYSE + NASDAQ). All data is sourced from Polygon.
// If Polygon fails, functions return null — UI handles the empty state.
// ============================================================================

import { getAllSecurities, getCategories } from '../data/dynamicSecurities';
import { hasApiKeys } from './apiConfig';
import * as polygon from './polygonService';
import { isBlockedEtp } from './leveragedEtpBlocklist';
import { scoreTickerSignal } from './signalScorer';
import { apiGetCalibration, apiGetVix } from './backendApi';
export { scoreTickerSignal };

export function getActiveSecs() { return getAllSecurities(); }
export function getActiveCats() { return getCategories(); }

// ── T1.3: client-side reliability-curve mapping ──
// Mirror of server/utils/calibrationCurve.js::applyCurve. Same bin layout
// (5-pt bins across [10, 95)), same snake_case row schema. The /signals page
// fetches the curve once per mount via apiGetCalibrationCurvePublic() and
// passes it here for every validated signal so the displayed confidence is
// the historically observed WR at that score. Server + client must stay in
// lockstep on bin math.
const CALIB_BIN_WIDTH    = 5;
const CALIB_BIN_MIN_LOW  = 10;
const CALIB_BIN_MAX_HIGH = 95;
export function applyCalibrationCurve(rawConfidence, curve) {
  const conf = Number(rawConfidence);
  if (!Number.isFinite(conf) || !Array.isArray(curve) || curve.length === 0) {
    return { mapped: Math.round(conf || 0), bin: null };
  }
  const clamped = Math.min(CALIB_BIN_MAX_HIGH - 1, Math.max(CALIB_BIN_MIN_LOW, Math.floor(conf)));
  const binLow  = Math.floor((clamped - CALIB_BIN_MIN_LOW) / CALIB_BIN_WIDTH) * CALIB_BIN_WIDTH + CALIB_BIN_MIN_LOW;
  const row = curve.find(r => Number(r.bin_low ?? r.binLow) === binLow);
  if (!row) return { mapped: Math.round(conf), bin: null };
  const identity      = !!(row.identity_mapped ?? row.identityMapped);
  const observedWrPct = row.observed_wr_pct ?? row.observedWrPct;
  const bin = {
    binLow,
    binHigh:           Number(row.bin_high ?? row.binHigh),
    n:                 Number(row.n) || 0,
    rawN:              Number(row.raw_n ?? row.rawN ?? 0),
    observedWrPct:     observedWrPct == null ? null : Number(observedWrPct),
    rawObservedWrPct:  Number(row.raw_observed_wr_pct ?? row.rawObservedWrPct ?? 0),
    identityMapped:    identity,
  };
  if (identity || bin.observedWrPct == null) return { mapped: Math.round(conf), bin };
  return { mapped: Math.round(bin.observedWrPct), bin };
}

// ── T4.1: position-size suggestion ──────────────────────────────────────────
// Risk-of-equity sizing. We risk a fixed % of the user's cash balance per
// trade so a stop-out is bounded — without this, one oversized losing trade
// can wipe ten small wins regardless of how accurate the signals are.
//
//   stocks  : risk_per_share    = entry - stop  (absolute, direction-agnostic)
//             qty               = floor(risk_dollars / risk_per_share)
//   options : risk_per_contract = premium × 100 × OPTION_MAX_DRAWDOWN_PCT
//             contracts         = floor(risk_dollars / risk_per_contract)
//             (mirrors the existing −50% premium-drawdown EXIT rule in
//              signalHealth.js — that IS our practical stop on options).
//
// Returns null when sizing isn't possible (no cash, no stop, no premium).
// `signal` may be a /signals-page signal (stop+stopPct on root) or a digest
// row (stop only). Both shapes resolved via the helper.
export const RISK_PER_TRADE_PCT      = 0.0075;   // 0.75% of equity per trade (fixed-mode fallback)
export const OPTION_MAX_DRAWDOWN_PCT = 0.50;     // matches the −50% EXIT rule
export const MAX_SUGGESTED_QTY       = 10_000;   // sanity cap
// Half-Kelly cap. Full Kelly maximises geometric growth in theory but is
// pathologically sensitive to estimation error in win-rate / R:R — a 50%
// confidence reading that's actually 45% will draw down ~30% before
// recovering. Half-Kelly cuts that volatility by ~75% with only ~25%
// less long-run growth (Thorp 2006; "The Kelly Capital Growth Investment
// Criterion"). The 2.5% hard cap matches the per-trade ceiling
// professional systematic shops typically run.
export const KELLY_FRACTION          = 0.5;
export const KELLY_MAX_FRACTION      = 0.025;    // 2.5% of equity max per trade

/**
 * Half-Kelly fraction for a single bet, capped at KELLY_MAX_FRACTION.
 * Returns null when the inputs are missing or implausible; returns 0
 * for negative-edge bets so the caller can fall through to the fixed
 * default rather than size to nothing.
 *
 *   f* = (p × b − q) / b
 *
 * where p is win probability (0-1), q = 1 − p, and b = R:R.
 */
export function kellyFraction(winRatePct, riskReward) {
  if (!Number.isFinite(winRatePct) || winRatePct <= 0 || winRatePct >= 100) return null;
  if (!Number.isFinite(riskReward) || riskReward <= 0) return null;
  const p = winRatePct / 100;
  const q = 1 - p;
  const b = riskReward;
  const fStar = (p * b - q) / b;
  if (fStar <= 0) return 0;       // no edge → caller falls back to fixed pct
  return Math.min(KELLY_MAX_FRACTION, KELLY_FRACTION * fStar);
}

function resolveStop(signal) {
  if (Number.isFinite(signal?.stop))     return Number(signal.stop);
  if (Number.isFinite(signal?.stopLoss)) return Number(signal.stopLoss);
  if (Number.isFinite(signal?.stopPct) && Number.isFinite(signal?.currentPrice)) {
    // stopPct can be signed (negative for LONG); use abs.
    return Number(signal.currentPrice) * (1 - Math.abs(Number(signal.stopPct)) / 100);
  }
  return null;
}

export function computeSuggestedPositionSize(signal, cashBalance, opts = {}) {
  const cash = Number(cashBalance);
  if (!Number.isFinite(cash) || cash <= 0) return null;

  // Pick the equity-risk fraction. Order of preference:
  //   1. Caller-supplied opts.equityRiskPct — explicit override wins.
  //   2. Kelly fraction from the signal's validated win rate + R:R, when
  //      both are present and produce a positive edge. This is the new
  //      default — sizing each trade in proportion to its edge lifts
  //      compounded returns at the same max drawdown vs a flat 0.75%.
  //   3. Fixed RISK_PER_TRADE_PCT fallback for manual trades or signals
  //      that haven't been validated yet (no winRate available).
  const overrideRiskPct = Number.isFinite(Number(opts.equityRiskPct))
    ? Number(opts.equityRiskPct)
    : null;
  const winRatePct = Number(signal?.confidence);  // displayed conf doubles as our best WR estimate
  const rr         = Number(signal?.riskReward);
  const kelly      = overrideRiskPct == null ? kellyFraction(winRatePct, rr) : null;
  let equityRiskPct;
  let sizingMode;
  if (overrideRiskPct != null && overrideRiskPct > 0) {
    equityRiskPct = overrideRiskPct;
    sizingMode    = 'override';
  } else if (Number.isFinite(kelly) && kelly > 0) {
    equityRiskPct = kelly;
    sizingMode    = 'kelly';
  } else {
    equityRiskPct = RISK_PER_TRADE_PCT;
    sizingMode    = 'fixed';
  }
  const riskDollars = cash * equityRiskPct;

  const isOption = signal?.type === 'option' || signal?.asset_type === 'option' || !!signal?.option;
  if (isOption) {
    // For options the entry price is the premium per share (×100 per contract).
    const premium = Number(signal?.option?.premium ?? signal?.entry_price ?? signal?.price);
    if (!Number.isFinite(premium) || premium <= 0) return null;
    const riskPerContract = premium * 100 * OPTION_MAX_DRAWDOWN_PCT;
    if (riskPerContract <= 0) return null;
    const qty = Math.max(1, Math.min(MAX_SUGGESTED_QTY, Math.floor(riskDollars / riskPerContract)));
    return {
      qty,
      riskDollars:  +(qty * riskPerContract).toFixed(2),
      basis:        'option-50pct-drawdown',
      riskPerUnit:  +riskPerContract.toFixed(2),
      equityRiskPct: +equityRiskPct.toFixed(4),
      sizingMode,
    };
  }

  const entry = Number(signal?.currentPrice ?? signal?.price ?? signal?.entry_price);
  const stop  = resolveStop(signal);
  if (!Number.isFinite(entry) || !Number.isFinite(stop)) return null;
  const riskPerShare = Math.max(0.01, Math.abs(entry - stop));
  const qty = Math.max(1, Math.min(MAX_SUGGESTED_QTY, Math.floor(riskDollars / riskPerShare)));
  return {
    qty,
    riskDollars:  +(qty * riskPerShare).toFixed(2),
    basis:        'stock-entry-minus-stop',
    riskPerUnit:  +riskPerShare.toFixed(2),
    equityRiskPct: +equityRiskPct.toFixed(4),
    sizingMode,
  };
}

// Keys live on the server — Polygon is always available from the frontend's perspective
const usePolygon = () => true;

// ── Liquidity floor ──
// $25M / day dollar volume. Picked as a balance: $10M (the pre-Phase-A floor)
// let too many ugly small-caps through where bid/ask spreads erode the edge;
// $50M (the brief Phase-A floor) was the institutional-desk minimum, which
// over-trimmed the universe for a retail account that trades on Robinhood.
// $25M keeps the names liquid enough for real-money fills while preserving
// most of the candidate pool entering the gates.
const MIN_DOLLAR_VOLUME_USD = 25_000_000;

// ── Caches ──
const historyCache = new Map();
const intradayCache = new Map();
const techCache = new Map();
const signalCache = new Map();
// quoteCache is keyed by ticker (not URL) so batch and individual lookups share the same entry.
// getBatchQuotes populates it; getQuote checks it first. TTL matches apiFetch quote TTL (15 s).
const quoteCache = new Map();
const CACHE_TTL = { history: 5 * 60 * 1000, intraday: 2 * 60 * 1000, tech: 5 * 60 * 1000, signals: 10 * 60 * 1000, quote: 15 * 1000 };

// ── Per-scan filter stats ──
// Every gate inside scoreCandidate increments a counter so the UI can show
// "scanned 8k → 23 candidates → 3 signals · 18 filtered (12 earnings · …)".
// Cleared at the start of every generateTradeSignals call.
let lastFilterStats = null;
function emptyFilterStats() {
  return {
    exchange:        'NYSE',
    universeRaw:     0,   // Polygon's raw grouped-daily row count (full US universe)
    universeSize:    0,   // post-liquidity-floor count (price ≥ $5, volume ≥ 500k, open > 0)
    candidatePool:   0,
    scored:          0,
    shipped:         0,
    // Pre-scoring filter: leveraged / inverse / volatility ETPs are dropped
    // BEFORE the candidate pool is ranked, so they're not part of `scored`
    // and stay out of `drops` (which tracks per-candidate rejections during
    // scoring). The "shipped + Σdrops === scored" invariant is preserved.
    leveragedEtpFiltered: 0,
    atrCoverage:          0,   // # tickers with a computed 10-day ATR baseline (vol-adjusted ranking)
    drops: {
      hold:              0,
      scoreLow:          0,
      earningsBlackout:  0,
      earningsNews:      0,
      confidenceLow:     0,
      regimeFiltered:    0,
      counterTrendLong:  0,
      counterTrendShort: 0,
      sectorFiltered:    0,  // LONG in lagging sector / SHORT in leading sector
      sectorCapped:      0,  // dropped because the sector already had 3 stronger signals
      negativeEV:        0,  // (cluster × direction) bucket has shown loss-making expectancy
      rrLow:             0,
      exception:         0,
    },
    fallback:        false, // true when grouped-daily failed → curated list
    fallbackReason:  null,  // the original error message, for debugging
    // Volatility regime info attached to every scan so the Signals page
    // footer can show "VIX 18.2 · normal regime" and the user can verify the
    // gate is actually firing. vixLevel is the raw Polygon value, vixRegime
    // is 'calm' | 'normal' | 'stressed' | 'unknown' (when VIX fetch failed).
    vixLevel:        null,
    vixRegime:       'unknown',
    gates:           null,  // { minScore, minConfidence, longCounterTrendScoreMin }
    timestamp:       0,
  };
}

export function getLastFilterStats() {
  return lastFilterStats;
}

// ============================================================================
// QUOTE — Polygon → null
// ============================================================================
export async function getQuote(ticker, { bypassCache = false } = {}) {
  // Serve from shared quoteCache if data is still fresh (same TTL as apiFetch quote layer).
  // This allows getBatchQuotes results to satisfy individual getQuote calls without extra API hits.
  const cached = quoteCache.get(ticker);
  if (!bypassCache && cached && Date.now() - cached.ts < CACHE_TTL.quote) return cached.data;

  if (usePolygon()) {
    try {
      const q = await polygon.getQuote(ticker, { force: bypassCache });
      if (q && q.price > 0) {
        const result = { ...q, _source: 'polygon', _fetchedAt: Date.now() };
        quoteCache.set(ticker, { data: result, ts: Date.now() });
        return result;
      }
    } catch (e) { /* fall through */ }
  }

  return null;
}

// ============================================================================
// BATCH QUOTES — single Polygon batch call
// ============================================================================
export async function getBatchQuotes(tickers) {
  const results = {};
  if (!tickers || tickers.length === 0) return results;
  const now = Date.now();

  if (usePolygon()) {
    try {
      const pq = await polygon.getBatchQuotes(tickers);
      Object.entries(pq).forEach(([sym, q]) => {
        const entry = { ...q, _source: 'polygon', _fetchedAt: now };
        results[sym] = entry;
        quoteCache.set(sym, { data: entry, ts: now });
      });
    } catch (e) { /* fall through to individual */ }
  }

  // Fallback: any ticker not yet resolved gets an individual call
  const remaining = tickers.filter(t => !results[t]);
  if (remaining.length > 0) {
    await Promise.all(remaining.map(async (t) => {
      const q = await getQuote(t);
      if (q) results[t] = q;
    }));
  }

  return results;
}

// ============================================================================
// HISTORY — Polygon → null
// Validates candle prices against live quote (rejects >25% off)
// ============================================================================
export async function getHistory(ticker, days = 90) {
  const cacheKey = `${ticker}:${days}`;
  const cached = historyCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < CACHE_TTL.history) return cached.data;

  let realPrice = null;
  try {
    const q = await getQuote(ticker);
    if (q) realPrice = q.price;
  } catch (e) { /* skip */ }

  const isReasonable = (candles) => {
    if (!realPrice || !candles || candles.length === 0) return true;
    const last = candles[candles.length - 1]?.close;
    return !last || Math.abs(last - realPrice) / realPrice < 0.25;
  };

  let result = null;
  if (usePolygon()) {
    try {
      const c = await polygon.getHistory(ticker, days);
      if (c?.length > 0 && isReasonable(c)) { result = c; }
    } catch (e) { /* */ }
  }

  if (result) {
    result._source = 'polygon';
    result._fetchedAt = Date.now();
    historyCache.set(cacheKey, { data: result, ts: Date.now() });
  }
  return result; // null if no data
}

// ============================================================================
// INTRADAY — Polygon → null
// ============================================================================
export async function getIntraday(ticker) {
  const cached = intradayCache.get(ticker);
  if (cached && Date.now() - cached.ts < CACHE_TTL.intraday) return cached.data;

  let realPrice = null;
  try {
    const q = await getQuote(ticker);
    if (q) realPrice = q.price;
  } catch (e) { /* */ }

  const isReasonable = (candles) => {
    if (!realPrice || !candles || candles.length === 0) return true;
    const last = candles[candles.length - 1]?.price || candles[candles.length - 1]?.close;
    return !last || Math.abs(last - realPrice) / realPrice < 0.25;
  };

  let result = null;
  if (usePolygon()) {
    try {
      const c = await polygon.getIntraday(ticker, 5);
      if (c?.length > 0 && isReasonable(c)) result = c;
    } catch (e) { /* */ }
  }

  if (result) intradayCache.set(ticker, { data: result, ts: Date.now() });
  return result; // null if no data
}

// ============================================================================
// TECHNICALS — Polygon-only, computed server-side from daily + weekly bars.
// ============================================================================
export async function getTechnicals(ticker, { bypassCache = false } = {}) {
  const cached = techCache.get(ticker);
  if (!bypassCache && cached && Date.now() - cached.timestamp < CACHE_TTL.tech) return cached.data;

  // Delegate to the Polygon-native batch endpoint (server computes every
  // indicator locally from daily + weekly bars).
  if (usePolygon()) {
    try {
      const batch = await polygon.getBatchTechnicals([ticker]);
      const assembled = batch?.[ticker];
      if (assembled) {
        const result = {
          ...assembled,
          // Pad fields the polygonTechnicals shape doesn't compute, so
          // downstream code that destructures these keeps working.
          macdHist:              assembled.macdHist              ?? null,
          sma20:                 null,
          sma50:                 null,
          atr:                   null,
          impliedVolatility:     null,
          historicalVolatility:  null,
          _source:               assembled._source || 'polygon',
        };
        techCache.set(ticker, { data: result, timestamp: Date.now() });
        return result;
      }
    } catch (e) { /* fall through to empty result */ }
  }
  const empty = {
    rsi: null, macd: null, macdSignal: null, macdHist: null,
    sma20: null, sma50: null,
    bollingerUpper: null, bollingerLower: null, bollingerMid: null,
    atr: null, impliedVolatility: null, historicalVolatility: null,
    stochK: null, stochD: null, supertrendUp: null, ema50: null,
    adx: null, obvRising: null, rsiWeekly: null, macdWeekly: null, macdSignalWeekly: null,
    _source: null,
  };
  techCache.set(ticker, { data: empty, timestamp: Date.now() });
  return empty;
}

// ============================================================================
// SHARED TARGET / STOP CALCULATOR
// Hard 2:1 R:R: target = 2× stop in every code path. Positive expectancy at a
// realistic 50–55% win rate requires R:R ≥ 2 after slippage and commission;
// sub-2:1 setups are mathematically near-impossible to be profitable.
// ============================================================================
export function computeTargetStop(q, tech, direction) {
  const price = (q && q.price) || 0;
  const holdingDays = (tech.rsi != null && (tech.rsi < 35 || tech.rsi > 65)) ? 5 : 3;
  const rsiExtremity = tech.rsi != null
    ? Math.max(0, Math.min(1, tech.rsi < 30 ? (30 - tech.rsi) / 20 : tech.rsi > 70 ? (tech.rsi - 70) / 20 : 0))
    : 0;
  const dailyRangePct = (q.high || 0) > 0 && (q.low || 0) > 0 && price > 0
    ? Math.max(1.5, Math.min(10.0, ((q.high - q.low) / price) * 100))
    : null;
  const stopPct   = dailyRangePct != null ? +(dailyRangePct * 1.5).toFixed(2) : +(2 + rsiExtremity).toFixed(2);
  const targetPct = +(stopPct * 2).toFixed(2);
  const timeframe = `Swing (${holdingDays}d)`;
  if (direction === 'LONG') {
    return { targetPrice: price > 0 ? +(price * (1 + targetPct / 100)).toFixed(2) : null, stopLoss: price > 0 ? +(price * (1 - stopPct / 100)).toFixed(2) : null, targetPct: +targetPct.toFixed(2), stopPct: +(-stopPct).toFixed(2), riskReward: +(targetPct / stopPct).toFixed(2), timeframe, holdingDays };
  }
  return { targetPrice: price > 0 ? +(price * (1 - targetPct / 100)).toFixed(2) : null, stopLoss: price > 0 ? +(price * (1 + stopPct / 100)).toFixed(2) : null, targetPct: +(-targetPct).toFixed(2), stopPct: +stopPct.toFixed(2), riskReward: +(targetPct / stopPct).toFixed(2), timeframe, holdingDays };
}

// ============================================================================
// BATCH TECHNICALS
//
// 1 HTTP call to /api/market/technicals, which fetches Polygon daily +
// weekly bars per ticker and computes every indicator locally (RSI,
// MACD, Bollinger, Stoch, ADX, OBV, SuperTrend, EMA50, weekly RSI/MACD).
// ============================================================================
export async function getBatchTechnicals(tickers) {
  if (!tickers || tickers.length === 0) return {};
  const now = Date.now();

  if (usePolygon()) {
    try {
      const polyBatch = await polygon.getBatchTechnicals(tickers);
      const results = {};
      for (const ticker of tickers) {
        const p = polyBatch[ticker];
        const assembled = p
          ? { ...p, sma20: null, sma50: null, atr: null, impliedVolatility: null, historicalVolatility: null,
              _source: 'polygon' }
          : { rsi: null, macd: null, macdSignal: null, bollingerUpper: null, bollingerLower: null,
              stochK: null, stochD: null, supertrendUp: null, ema50: null,
              rsiWeekly: null, macdWeekly: null, macdSignalWeekly: null,
              adx: null, obvRising: null, _source: null };
        results[ticker] = assembled;
        techCache.set(ticker, { data: assembled, timestamp: now });
      }
      return results;
    } catch (e) {
      console.warn('[Signals] /technicals failed, falling back per-ticker:', e?.message || e);
      // fall through to individual getTechnicals
    }
  }

  // Last-resort fallback — per-ticker getTechnicals.
  const results = {};
  await Promise.all(tickers.map(async (ticker) => {
    results[ticker] = await getTechnicals(ticker).catch(() => ({}));
  }));
  return results;
}

// ============================================================================
// COMPANY PROFILE
// ============================================================================
export async function getProfile(ticker) {
  if (usePolygon()) {
    try { return await polygon.getCompanyProfile(ticker); } catch (e) { /* */ }
  }
  // Fallback: local curated universe for name/sector metadata
  const sec = getAllSecurities().find(s => s.ticker === ticker);
  return { ticker, name: sec?.name || ticker, industry: sec?.sector || 'Unknown' };
}

// ============================================================================
// MARKET NEWS
// ============================================================================
export async function getMarketNews() {
  if (usePolygon()) {
    try { return await polygon.getMarketNews(); } catch (e) { /* */ }
  }
  return [];
}

// ============================================================================
// P&L PROBABILITY (normal distribution math — not simulated)
// ============================================================================
// driftPct: expected return over the period (e.g. 0.02 = +2% expected). Default 0 = symmetric.
export function computePlProbability(price, iv = 30, days = 5, driftPct = 0) {
  const sigma = (iv / 100), dailyMove = sigma / Math.sqrt(252), periodMove = dailyMove * Math.sqrt(days);
  const arr = [];
  for (let pct = -periodMove * 3; pct <= periodMove * 3; pct += periodMove * 0.2) {
    // Shift the distribution by driftPct so the peak sits at the expected return
    const z = (pct - driftPct) / periodMove;
    const prob = Math.exp(-0.5 * z ** 2) / (periodMove * Math.sqrt(2 * Math.PI));
    arr.push({
      returnPct: +(pct * 100).toFixed(2),
      priceAt: +(price * (1 + pct)).toFixed(2),
      probability: prob,
      profit: +(price * pct).toFixed(2),
    });
  }
  let tot = 0; arr.forEach(x => tot += x.probability);
  let cum = 0; arr.forEach(x => { x.probability /= tot; cum += x.probability; x.cumProb = cum; });
  return arr;
}

// ============================================================================
// NEWS SENTIMENT SCORER
// Prefers Polygon's LLM sentiment (positive/negative/neutral per ticker).
// Falls back to keyword matching when Polygon insights are not populated
// (requires a paid Polygon plan tier).
// ============================================================================
const BULLISH_WORDS = [
  'beat', 'beats', 'surge', 'surges', 'soar', 'soars', 'rally', 'rallies',
  'profit', 'profits', 'growth', 'record', 'upgrade', 'upgrades', 'strong',
  'outperform', 'expand', 'launch', 'launches', 'win', 'wins', 'gain', 'gains',
  'positive', 'buy', 'bullish', 'breakout', 'recovery', 'boost', 'raised',
];
const BEARISH_WORDS = [
  'miss', 'misses', 'decline', 'declines', 'fall', 'falls', 'plunge', 'plunges',
  'loss', 'losses', 'downgrade', 'downgrades', 'weak', 'recall', 'lawsuit',
  'underperform', 'layoff', 'layoffs', 'debt', 'warn', 'warns', 'cut', 'cuts',
  'negative', 'sell', 'bearish', 'breakdown', 'concern', 'probe', 'investigation',
];

// ── Earnings detector ──
// Recent news headlines mentioning an earnings event are a strong proxy for
// "earnings within the last 3 days or upcoming imminently" — either case
// dwarfs the technical edge with overnight gap risk. Polygon's news endpoint
// already covers a 3-day window, so any hit here puts us inside the blackout.
const EARNINGS_PATTERNS = [
  /\bearnings\b/i,
  /\bquarterly\s+(results?|earnings|report)/i,
  /\bq[1-4]\s+(results?|earnings|report)/i,
  /\breports?\s+(earnings|results?)\b/i,
  /\bearnings\s+(call|preview|beat|miss|date|surprise)/i,
  /\bfiscal\s+q[1-4]\b/i,
  /\bbeats?\s+estimates?\b/i,
  /\bmisses?\s+estimates?\b/i,
];

export function hasEarningsContext(articles) {
  if (!articles || articles.length === 0) return false;
  // Top 5 most recent (news is already sorted desc by published_utc); broader
  // windows pick up retrospective coverage that doesn't reflect imminent risk.
  const recent = articles.slice(0, 5);
  for (const article of recent) {
    const text = `${article.headline || ''} ${article.summary || ''}`;
    if (EARNINGS_PATTERNS.some(re => re.test(text))) return true;
  }
  return false;
}

export function scoreNewsSentiment(articles) {
  if (!articles || articles.length === 0) return { score: 0, label: 'Neutral', count: 0 };

  const recent = articles.slice(0, 5);

  // Use Polygon LLM sentiment when available (non-neutral entries present)
  const hasLLMSentiment = recent.some(a => a.sentiment === 'positive' || a.sentiment === 'negative');
  let total = 0;

  if (hasLLMSentiment) {
    for (const article of recent) {
      if (article.sentiment === 'positive') total += 1;
      else if (article.sentiment === 'negative') total -= 1;
    }
  } else {
    // Fallback: keyword scoring on headline + summary
    for (const article of recent) {
      const text = `${article.headline || ''} ${article.summary || ''}`.toLowerCase();
      let s = 0;
      BULLISH_WORDS.forEach(w => { if (text.includes(w)) s++; });
      BEARISH_WORDS.forEach(w => { if (text.includes(w)) s--; });
      total += Math.sign(s);
    }
  }

  const score = total / recent.length; // -1 to +1
  const label = score > 0.2 ? 'Bullish' : score < -0.2 ? 'Bearish' : 'Neutral';
  return { score, label, count: recent.length };
}

// ============================================================================
// TRADE SIGNAL GENERATOR — real data only, no simulated signals
//
// Two-pass scan:
//   Pass 1 — Polygon grouped-daily: fetch ALL US stocks (~8-10k) in one call,
//             filter by price/volume, rank top 50 by momentum score.
//   Pass 2 — server-side technicals: fetch every indicator for those 50.
//   Score & return top maxSignals.
// ============================================================================

// Score a raw Polygon bar by how "interesting" it is for signal generation.
// Heavily weights big price moves; volume gives a smaller bonus.
// Candidate-pool ranking score — a composite of the market-analyst factors
// we've validated data for so far. Higher = more likely to be scored for a
// signal. Two live factors (more to come: trend-structure, level-proximity,
// informed-flow, + a reserved quiet-high-structure slice):
//
//   1. VOLATILITY-ADJUSTED MOVE (atrUnits) — daily % change ÷ the stock's own
//      10-day ATR%. "How many normal days of movement happened today": a 2%
//      move on a 1%-ATR name (2 ATRs, unusual) beats a 2% move on an 8%-ATR
//      name (0.25 ATR, noise). Stops the pool being dominated by perennially-
//      volatile names and surfaces meaningful moves on calmer stocks.
//
//   2. RELATIVE VOLUME (rvol) — today's volume ÷ the stock's own 10-day avg.
//      Catches unusual participation — where institutions are actually
//      active — INDEPENDENT of price move. A stock quietly trading 3× its
//      normal volume is being accumulated/distributed even if price barely
//      budged; raw-% ranking would miss it entirely. This is what lets a
//      calm-but-active name earn its way into the pool.
//
// Weighting: a 1-ATR move contributes ~3; a 3× RVOL contributes ~3 (rvol−1
// so "normal" 1× adds nothing, ×1.5 slope, capped so a halted-then-resumed
// 50× spike can't dominate). Roughly co-equal, matching the analyst
// blueprint (~0.20 vol-adj move / ~0.30 RVOL of the eventual 5-factor mix).
//
// Fallbacks: missing atrPct → raw |changePct| for that term; missing rvol →
// legacy volume bonus. A ticker is never silently dropped for lacking a
// baseline.
function momentumScore(r) {
  const absMove = Math.abs(r.changePct || 0);

  // Term 1 — volatility-adjusted move (or raw move when no ATR baseline).
  const moveTerm = (Number.isFinite(r.atrPct) && r.atrPct > 0.1)
    ? (absMove / r.atrPct) * 3
    : absMove;

  // Term 2 — relative volume (or the legacy coarse volume bonus as fallback).
  let volTerm;
  if (Number.isFinite(r.rvol) && r.rvol > 0) {
    // Excess over "normal" (1×), 1.5 slope, capped at 8× so extreme spikes
    // (halt resumptions, index-rebalance prints) can't hijack the ranking.
    volTerm = Math.max(0, Math.min(8, r.rvol) - 1) * 1.5;
  } else {
    volTerm = r.volume > 5_000_000 ? 3 : r.volume > 1_000_000 ? 1 : 0;
  }

  return moveTerm + volTerm;
}

// Build an option signal object from a real Polygon chain entry.
// Uses ask price as the premium (actual cost to enter), falling back to midpoint/last.
function buildRealOption(realOpt, dir, iv) {
  const premium = realOpt.ask || realOpt.midpoint || realOpt.last;
  const spread = (realOpt.bid != null && realOpt.ask != null)
    ? +((realOpt.ask - realOpt.bid) / realOpt.ask).toFixed(2)
    : null;
  const optType = realOpt.type.toUpperCase();
  return {
    type: optType, strike: realOpt.strike, symbol: realOpt.symbol ?? null,
    expiration: realOpt.expiration,
    expirationDays: Math.round((new Date(realOpt.expiration) - new Date()) / 86400000),
    premium, bid: realOpt.bid, ask: realOpt.ask, spreadPct: spread,
    delta: realOpt.delta, gamma: realOpt.gamma, theta: realOpt.theta, vega: realOpt.vega,
    iv: realOpt.iv ? +(realOpt.iv * 100).toFixed(1) : iv,
    volume: realOpt.volume, openInterest: realOpt.openInterest,
    moneyness: realOpt.strike === realOpt.underlyingPrice ? 'ATM' :
      (optType === 'CALL'
        ? (realOpt.underlyingPrice > realOpt.strike ? 'ITM' : 'OTM')
        : (realOpt.underlyingPrice < realOpt.strike ? 'ITM' : 'OTM')),
    breakeven: optType === 'CALL'
      ? +(realOpt.strike + premium).toFixed(2)
      : +(realOpt.strike - premium).toFixed(2),
    strategy: iv > 50 ? 'High IV — consider debit spread' : iv > 35 ? 'Moderate IV — debit spread' : iv < 18 ? 'Low IV — direct buy' : 'Balanced IV',
    _source: 'polygon',
  };
}

// ── Options-native signal evaluation ─────────────────────────────────────────
// Replaces the (misleading) practice of showing the UNDERLYING stock's
// backtest win-rate on an option. An option's P&L depends on direction AND
// magnitude AND speed AND volatility — the stock's directional win-rate says
// nothing about whether the option makes money. This computes options-native
// metrics from data we actually have (chain Greeks + IV + the underlying's
// realized vol):
//
//   • POP (probability of profit) — from the IV-implied price distribution at
//     the holding horizon, the fraction of outcomes where the option's
//     first-order (delta-theta) P&L is positive. A REAL options number, not
//     the stock's win-rate.
//   • EV — expected option P&L over the same distribution, as a % of premium.
//     The standard trading-desk screen: EV ≈ Σ prob(P)·[δ·(P−spot) + θ·days].
//   • ivRich — current IV vs the underlying's realized (historical) vol. On
//     Polygon Starter we can't get 52-week IV rank, but IV-vs-realized is the
//     legitimate "volatility risk premium" proxy: IV ≫ realized = paying up
//     for expensive vol (IV-crush risk on a long); IV ≤ realized = cheap.
//   • verdict — 'favorable' | 'marginal' | 'unfavorable', used to GATE which
//     options get attached to a signal at all.
//
// `driftPct` credits the signal's directional edge: we shift the price
// distribution by a fraction of the forecast move in the thesis direction so
// EV isn't a driftless coin-flip (which would understate a real edge) but also
// doesn't assume the target is always hit (which would overstate it). Default
// 0 = fully conservative.
//
// Theta convention: Polygon theta is negative for long options (daily decay),
// so θ·days is a cost. delta is signed (+ for calls, − for puts), and the
// distribution nodes span both sides of spot, so puts profit on down-moves
// naturally without special-casing.
export function evaluateOptionSignal(option, { spotPrice, holdingDays = 5, realizedVolPct = null, driftPct = 0 } = {}) {
  if (!option || !Number.isFinite(spotPrice) || spotPrice <= 0) return null;
  const premium = Number(option.premium);
  const delta   = Number(option.delta);
  const theta   = Number(option.theta);
  const iv      = Number(option.iv);   // percent, e.g. 45.0
  if (!Number.isFinite(premium) || premium <= 0) return null;

  // Cap the modeling horizon at the option's remaining life — a 5-day swing on
  // a 3-DTE option only has 3 days of theta to pay.
  const days = Math.max(1, Math.min(Number(holdingDays) || 5, option.expirationDays || 21));

  const distIv = Number.isFinite(iv) && iv > 0 ? iv : 30;
  const dist = computePlProbability(spotPrice, distIv, days, driftPct);

  let ev = 0, popMass = 0;
  const haveGreeks = Number.isFinite(delta) && Number.isFinite(theta);
  if (haveGreeks) {
    for (const node of dist) {
      // First-order (delta-theta) option P&L per share at this underlying node.
      const pnl = delta * (node.priceAt - spotPrice) + theta * days;
      ev += node.probability * pnl;
      if (pnl > 0) popMass += node.probability;
    }
  }
  const evPctOfPremium = haveGreeks && premium > 0 ? +(ev / premium * 100).toFixed(1) : null;
  const pop            = haveGreeks ? +(popMass * 100).toFixed(0) : null;

  // IV richness: current IV vs realized vol. >1.3× = rich (expensive), <0.9× =
  // cheap. Null when we lack a realized-vol reference.
  let ivRich = null;
  if (Number.isFinite(realizedVolPct) && realizedVolPct > 0 && Number.isFinite(iv) && iv > 0) {
    const ratio = iv / realizedVolPct;
    ivRich = ratio > 1.3 ? 'rich' : ratio < 0.9 ? 'cheap' : 'fair';
  }

  // Verdict gates attachment. Options are a higher bar than the stock: require
  // a positive expected value AND a coin-flip-or-better POP to call it
  // 'favorable'. 'marginal' still attaches but flags the risk; 'unfavorable'
  // is dropped by the caller so we never recommend an option expected to lose.
  let verdict;
  if (evPctOfPremium == null) {
    verdict = 'unknown';                       // no Greeks — can't evaluate
  } else if (evPctOfPremium > 0 && pop >= 45) {
    verdict = 'favorable';
  } else if (evPctOfPremium > -15) {
    verdict = 'marginal';
  } else {
    verdict = 'unfavorable';
  }

  return { pop, evDollars: haveGreeks ? +ev.toFixed(2) : null, evPctOfPremium, breakeven: option.breakeven, holdingDays: days, ivRich, verdict };
}

// V2 widens the forward earnings-blackout window from 5 → 10 days because a
// 5-day swing trade entered just before a 6-day-out earnings call has its
// target/stop completely overwritten by the post-earnings gap. The backwards
// window stays at 2 days (any earnings that just printed has already moved
// the stock; technicals reset quickly).
async function getEarningsBlackoutTickers() {
  try {
    const cal = await polygon.getEarningsCalendar({ daysBack: 2, daysForward: 10 });
    if (!cal) {
      console.log('[Signals] earningsBlackout size=0 source=none (Benzinga unavailable on this plan)');
      return new Set();
    }
    const set = new Set(Object.keys(cal).map(t => t.toUpperCase()));
    console.log(`[Signals] earningsBlackout size=${set.size} source=benzinga`);
    return set;
  } catch {
    console.log('[Signals] earningsBlackout size=0 source=none (fetch error)');
    return new Set();
  }
}

// VIX-aware regime gates. Mirrors server/config/signalV2.js so client + server
// agree on what 'stressed' means without an import dance through ESM.
//   VIX <  18 → calm     — defaults
//   VIX < 25  → normal   — defaults
//   VIX ≥ 25  → stressed — score ≥ 5, confidence ≥ 65, both counter-trend filters need score ≥ 10
function regimeGates(regime) {
  if (regime === 'stressed') return { minScore: 5, minConfidence: 65, longCounterTrendScoreMin: 10, shortCounterTrendScoreMin: 10 };
  return { minScore: 4, minConfidence: 55, longCounterTrendScoreMin: 8, shortCounterTrendScoreMin: 8 };
}

// ── Shared signal-gate evaluator ─────────────────────────────────────────────
// Single source of truth for "would this scored signal actually ship on the
// Signals page, and if not, why?". Mirrors the gate sequence inside
// scoreCandidate() so the Watchlist (which scores favorited tickers with a
// lenient score≥4/conf≥55 gate) can tell the user when a signal it's showing
// would be SUPPRESSED by the stricter Signals-page engine — instead of the
// two pages silently disagreeing.
//
// Returns { ships: boolean, reason: string|null, detail: string|null }.
// `reason` is a short machine-ish code; `detail` is a one-line human
// explanation suitable for a badge tooltip.
//
// IMPORTANT: this only models the GATE checks, not candidate-pool membership.
// A signal can pass every gate here and still never appear on the Signals
// page simply because the ticker wasn't in the top-momentum scan slice that
// day — that's surfaced separately as 'notInScan'.
export function evaluateSignalGates(scored, { regimeBull = true, vixRegime = 'normal', tech = {}, price = 0 } = {}) {
  const dir   = scored?.direction;
  const score = Math.abs(Number(scored?.score) || 0);
  const conf  = Number(scored?.confidence) || 0;

  if (dir !== 'LONG' && dir !== 'SHORT') {
    return { ships: false, reason: 'hold', detail: 'No directional signal — HOLD.' };
  }

  const gates = regimeGates(vixRegime);

  if (score < gates.minScore) {
    return { ships: false, reason: 'scoreLow', detail: `Score ${score} below the ${vixRegime}-regime floor of ${gates.minScore}.` };
  }
  if (conf < gates.minConfidence) {
    return { ships: false, reason: 'confidenceLow', detail: `Confidence ${Math.round(conf)}% below the ${vixRegime}-regime floor of ${gates.minConfidence}%.` };
  }

  // Bull-tape SHORT / bear-tape LONG suppression — fighting the index.
  if (!regimeBull && dir === 'LONG' && score < 6) {
    return { ships: false, reason: 'regimeFiltered', detail: `Counter-trend LONG in a bear market needs score ≥ 6 (has ${score}).` };
  }
  if (regimeBull && dir === 'SHORT' && score < 6) {
    return { ships: false, reason: 'regimeFiltered', detail: `Counter-trend SHORT in a bull market needs score ≥ 6 (has ${score}).` };
  }

  // Counter-trend stock-structure filters.
  if (dir === 'LONG' && score < gates.longCounterTrendScoreMin) {
    const aboveEma50   = tech.ema50 != null && price > tech.ema50;
    const supertrendUp = tech.supertrendUp === true;
    const weeklyMacdUp = tech.macdWeekly != null && tech.macdSignalWeekly != null && tech.macdWeekly > tech.macdSignalWeekly;
    if (!(aboveEma50 && (supertrendUp || weeklyMacdUp))) {
      return { ships: false, reason: 'counterTrendLong', detail: `LONG on a stock not in an uptrend (below EMA50 / no trend confirmation) needs score ≥ ${gates.longCounterTrendScoreMin}.` };
    }
  }
  if (dir === 'SHORT' && score < gates.shortCounterTrendScoreMin) {
    const belowEma50     = tech.ema50 != null && price < tech.ema50;
    const supertrendDown = tech.supertrendUp === false;
    const weeklyMacdDown = tech.macdWeekly != null && tech.macdSignalWeekly != null && tech.macdWeekly < tech.macdSignalWeekly;
    if (!(belowEma50 && (supertrendDown || weeklyMacdDown))) {
      return { ships: false, reason: 'counterTrendShort', detail: `SHORT on a stock that's still in an uptrend ("shorting strength") needs score ≥ ${gates.shortCounterTrendScoreMin}.` };
    }
  }

  return { ships: true, reason: null, detail: 'Passes the Signals-page quality gates.' };
}

// ── Sector Relative Strength ───────────────────────────────────────────────
// Compute each sector's average intraday move across the candidate pool,
// rank into quintiles, and return a map ticker → 'leading' | 'neutral' |
// 'lagging'. Used to gate signal direction:
//   leading sector  → SHORTs blocked (shorting strength historically loses)
//   lagging sector  → LONGs blocked  (longing weakness = falling-knife trade)
//   neutral sector  → both directions allowed
//
// A sector needs at least 3 candidates to be ranked — single-stock sectors
// are too noisy. Sectors with fewer candidates fall back to 'neutral'.
function computeSectorRSMap(candidates, sectorByTicker) {
  const bySector = new Map();
  for (const c of candidates) {
    const sector = sectorByTicker?.[c.ticker] ?? 'other';
    if (!bySector.has(sector)) bySector.set(sector, []);
    bySector.get(sector).push(c);
  }

  const ranked = [];
  for (const [sector, items] of bySector) {
    if (items.length < 3) continue;
    const avg = items.reduce((s, c) => s + (c.changePct || 0), 0) / items.length;
    ranked.push({ sector, avg, count: items.length });
  }
  ranked.sort((a, b) => b.avg - a.avg);

  const top    = Math.max(1, Math.ceil(ranked.length * 0.2));
  const bottom = Math.max(1, Math.ceil(ranked.length * 0.2));
  const sectorRank = new Map();
  for (let i = 0; i < ranked.length; i++) {
    if (i < top)                          sectorRank.set(ranked[i].sector, 'leading');
    else if (i >= ranked.length - bottom) sectorRank.set(ranked[i].sector, 'lagging');
    else                                  sectorRank.set(ranked[i].sector, 'neutral');
  }
  return sectorRank;
}

// Return false if the (direction, sector) combination is blocked by the
// sector RS gate. Unknown sectors and neutral-rank sectors always pass.
function sectorRSAllows(direction, sector, sectorRank) {
  const rank = sectorRank.get(sector);
  if (!rank || rank === 'neutral') return true;
  if (direction === 'LONG'  && rank === 'lagging') return false;
  if (direction === 'SHORT' && rank === 'leading') return false;
  return true;
}

// ── Confidence calibration via live track record ──────────────────────────
// Bayesian-shrink the heuristic confidence toward the observed win rate for
// the signal's (cluster × direction) bucket. With n=50+ scored outcomes the
// observed WR completely overrides the heuristic; below n=10 the calibration
// is skipped entirely (too thin to trust).
//
// Returns { calibratedConfidence, evPct, sampleN, observedWinRate } or null
// if no usable calibration data exists for the bucket.
// T3.1: regime-aware bucket lookup. Mirrors the server's
// digestSignals.lookupCalibrationBucket — prefer the regime-specific row when
// n ≥ 20, else fall back to the 'all' roll-up. Map is keyed
// `${cluster}:${direction}:${regime}` and always carries an 'all' entry.
const REGIME_BUCKET_MIN_N_CLIENT = 20;
function lookupCalibrationBucket(cluster, direction, regime, calibrationMap) {
  if (!cluster || !direction || !calibrationMap) return null;
  if (regime && regime !== 'all') {
    const specific = calibrationMap.get(`${cluster}:${direction}:${regime}`);
    if (specific && Number(specific.n) >= REGIME_BUCKET_MIN_N_CLIENT) return specific;
  }
  return calibrationMap.get(`${cluster}:${direction}:all`)
      ?? calibrationMap.get(`${cluster}:${direction}`)  // legacy pre-T3 map
      ?? null;
}

function applyCalibration(rawConfidence, cluster, direction, calibrationMap, regime = null) {
  const bucket = lookupCalibrationBucket(cluster, direction, regime, calibrationMap);
  if (!bucket || Number(bucket.n) < 10) return null;

  const weight = Math.min(1, Number(bucket.n) / 50);
  const observedWR = Number(bucket.win_rate_pct) || 0;
  const calibratedConfidence = +((1 - weight) * rawConfidence + weight * observedWR).toFixed(1);
  return {
    calibratedConfidence,
    evPct:            bucket.expectancy_pct != null ? Number(bucket.expectancy_pct) : null,
    sampleN:          Number(bucket.n) || 0,
    observedWinRate:  observedWR,
    regime:           bucket.regime ?? 'all',
  };
}

// Hard negative-EV gate. If the (cluster × direction × regime) bucket has
// ≥ 20 scored outcomes and the expectancy is < -0.5% per trade, the signal is
// dropped — we've measured that we lose money on this exact setup over the
// recent window. Conservative: thin samples (n<20) or barely-negative buckets
// pass through; only convincingly loss-making combos get killed.
function isNegativeEVBucket(cluster, direction, calibrationMap, regime = null) {
  const bucket = lookupCalibrationBucket(cluster, direction, regime, calibrationMap);
  if (!bucket || Number(bucket.n) < 20) return false;
  const ev = bucket.expectancy_pct != null ? Number(bucket.expectancy_pct) : null;
  return ev != null && ev < -0.5;
}

// ── Sector diversification cap ─────────────────────────────────────────────
// Greedy walk through the already-sorted signal list, dropping any that
// would push a sector past its cap. Preserves order (highest-EV first) so
// the top of the list is always the best signal in each sector.
function applySectorCap(signals, sectorByTicker, maxPerSector) {
  const counts = new Map();
  const kept = [];
  const dropped = [];
  for (const s of signals) {
    const sector = sectorByTicker?.[s.ticker] ?? 'other';
    const used = counts.get(sector) || 0;
    if (used >= maxPerSector) { dropped.push(s); continue; }
    counts.set(sector, used + 1);
    kept.push(s);
  }
  return { kept, dropped };
}

async function scoreCandidate({ ticker, name, category, categoryColor, q, tech, breadthRatio = 0.5, indexChangePct = 0, regimeBull = true, vixRegime = 'normal', earningsBlackoutSet = null, calibrationMap = null, stats = null }, out) {
  // The stats parameter is optional so callers that don't care still work.
  // Each gate below bumps its specific counter before returning so the UI can
  // explain *why* a low-count scan dropped the candidates it dropped.
  const bump = (key) => { if (stats) stats.drops[key] = (stats.drops[key] || 0) + 1; };
  if (stats) stats.scored = (stats.scored || 0) + 1;
  const isRealTech = tech?._source === 'polygon';

  // ── Pass 1: technical score (no external calls) ──
  // dir starts as 'HOLD' — direction must be explicitly set by a primary indicator
  // (RSI extreme, Bollinger breach, strong move, or MACD in neutral RSI zone).
  // Signals that never set a direction are suppressed at the gate below.
  let score = 0, dir = 'HOLD', reasons = [];

  if (tech.rsi != null) {
    if      (tech.rsi < 30) { dir = 'LONG';  score += 2; reasons.push(`RSI oversold (${tech.rsi.toFixed(0)})`); }
    else if (tech.rsi > 70) { dir = 'SHORT'; score += 2; reasons.push(`RSI overbought (${tech.rsi.toFixed(0)})`); }
    else if (tech.rsi < 40) { if (dir === 'HOLD') dir = 'LONG';  score += 1; reasons.push('RSI low zone'); }
    else if (tech.rsi > 60) { if (dir === 'HOLD') dir = 'SHORT'; score += 1; reasons.push('RSI high zone'); }
  }

  // MACD — only scores when it agrees with the direction already set by RSI.
  // If RSI is neutral (40-60) MACD may set direction. If MACD contradicts a
  // clear RSI reading, it earns no points (acts as a filter rather than a bonus).
  if (tech.macd != null && tech.macdSignal != null) {
    const macdBull = tech.macd > tech.macdSignal;
    const rsiNeutral = tech.rsi == null || (tech.rsi >= 40 && tech.rsi <= 60);
    if (macdBull) {
      if (dir === 'LONG')       { score += 1; reasons.push('MACD confirms bullish'); }
      else if (rsiNeutral)      { dir = 'LONG'; score += 1; reasons.push('MACD bullish'); }
      // else: contradicts RSI — no points
    } else {
      if (dir === 'SHORT')      { score += 1; reasons.push('MACD confirms bearish'); }
      else if (rsiNeutral)      { dir = 'SHORT'; score += 1; reasons.push('MACD bearish'); }
      // else: contradicts RSI — no points
    }
  }

  if (tech.bollingerLower != null && tech.bollingerUpper != null) {
    if      (q.price < tech.bollingerLower) { if (dir !== 'SHORT') dir = 'LONG';  score += 2; reasons.push('Below Bollinger Lower'); }
    else if (q.price > tech.bollingerUpper) { dir = 'SHORT'; score += 2; reasons.push('Above Bollinger Upper'); }
  }

  if (q.avgVolume > 0 && q.volume > q.avgVolume * 1.5) {
    score += 1; reasons.push('High volume');
  }

  // Strong intraday moves are LATE — by the time the signal ships, the move
  // has already happened. Never let a large move SET direction by itself
  // (that's how we end up chasing tops and shorting bottoms). It can only
  // CONFIRM a direction set by a durable indicator (RSI extreme, BB breach,
  // EMA-50 cross). Against-direction moves are actively penalised: catching
  // a falling knife (LONG into −2%) and shorting into strength (SHORT into
  // +2%) are two of the most common loss patterns.
  if (Math.abs(q.changePct || 0) > 1.5) {
    const up = q.changePct > 0;
    if (up && dir === 'LONG') {
      score += 1; reasons.push(`Confirmed by +${q.changePct.toFixed(1)}% today`);
    } else if (!up && dir === 'SHORT') {
      score += 1; reasons.push(`Confirmed by ${q.changePct.toFixed(1)}% today`);
    } else if (up && dir === 'SHORT') {
      score -= 1; // shorting into strength
    } else if (!up && dir === 'LONG') {
      score -= 1; // catching a falling knife
    }
  }

  // VWAP — price closing on the correct side of VWAP confirms institutional bias.
  if (q.vwap != null) {
    const vwapPct = +((q.price - q.vwap) / q.vwap * 100).toFixed(1);
    if      (dir === 'LONG'  && q.price > q.vwap) { score += 1; reasons.push(`Above VWAP +${vwapPct}%`); }
    else if (dir === 'SHORT' && q.price < q.vwap) { score += 1; reasons.push(`Below VWAP ${vwapPct}%`); }
  }

  // ADX — trend strength
  if (tech.adx != null) {
    if      (tech.adx < 20) { score -= 1; reasons.push(`Weak trend ADX ${tech.adx.toFixed(0)}`); }
    else if (tech.adx > 25) { score += 1; reasons.push(`Trend ADX ${tech.adx.toFixed(0)}`); }
  }

  // OBV — institutional accumulation/distribution
  if (tech.obvRising != null) {
    if      (dir === 'LONG'  && tech.obvRising)  { score += 1; reasons.push('OBV rising (accumulation)'); }
    else if (dir === 'SHORT' && !tech.obvRising) { score += 1; reasons.push('OBV falling (distribution)'); }
  }

  // Weekly RSI — multi-timeframe: daily signal is more reliable when the weekly
  // timeframe still has room to move in the same direction.
  if (tech.rsiWeekly != null) {
    if      (dir === 'LONG'  && tech.rsiWeekly < 60) { score += 1; reasons.push(`Weekly RSI ${tech.rsiWeekly.toFixed(0)}`); }
    else if (dir === 'SHORT' && tech.rsiWeekly > 40) { score += 1; reasons.push(`Weekly RSI ${tech.rsiWeekly.toFixed(0)}`); }
  }

  // Weekly MACD — second timeframe confirmation
  if (tech.macdWeekly != null && tech.macdSignalWeekly != null) {
    const wkMacdBull = tech.macdWeekly > tech.macdSignalWeekly;
    if      (dir === 'LONG'  && wkMacdBull)  { score += 1; reasons.push('Weekly MACD bullish'); }
    else if (dir === 'SHORT' && !wkMacdBull) { score += 1; reasons.push('Weekly MACD bearish'); }
  }

  // Market breadth — full 8k-stock universe (accurate).
  // Tailwind: >55% of market advancing → LONG gets +1; headwind → LONG gets -1.
  if (breadthRatio > 0.55) {
    if      (dir === 'LONG')  { score += 1; reasons.push(`${Math.round(breadthRatio * 100)}% market advancing`); }
    else if (dir === 'SHORT') { score -= 1; }
  } else if (breadthRatio < 0.45) {
    if      (dir === 'SHORT') { score += 1; reasons.push(`${Math.round((1 - breadthRatio) * 100)}% market declining`); }
    else if (dir === 'LONG')  { score -= 1; }
  }

  // ── Relative Strength vs Index (SPY) ──────────────────────────────────────
  // Stocks leading the index in the signal direction show genuine momentum;
  // those lagging suggest the move is index-driven.
  const rsVsIndex = (q.changePct || 0) - indexChangePct;
  if (dir === 'LONG') {
    if      (rsVsIndex > 2.0)  { score += 2; reasons.push(`RS +${rsVsIndex.toFixed(1)}% vs index`); }
    else if (rsVsIndex > 0.8)  { score += 1; reasons.push(`RS +${rsVsIndex.toFixed(1)}% vs index`); }
    else if (rsVsIndex < -2.5) { score -= 1; } // underperforming badly despite LONG signal
  } else {
    if      (rsVsIndex < -2.0) { score += 2; reasons.push(`RS ${rsVsIndex.toFixed(1)}% vs index`); }
    else if (rsVsIndex < -0.8) { score += 1; reasons.push(`RS ${rsVsIndex.toFixed(1)}% vs index`); }
    else if (rsVsIndex > 2.5)  { score -= 1; } // outperforming despite SHORT signal
  }
  // Confidence boost for strong RS
  const strongRS = (dir === 'LONG' && rsVsIndex > 2.0) || (dir === 'SHORT' && rsVsIndex < -2.0);

  // ── 52-Week High/Low Proximity ────────────────────────────────────────────
  // Price near the 52-week high during a LONG signal = confirmed breakout zone.
  // Price near the 52-week low during a SHORT signal = established downtrend.
  // Also rewards mean-reversion: LONG near 52-week low (deeply oversold absolute level).
  // week52High / week52Low are computed locally on the server from the same
  // daily bars that drive every other indicator.
  let near52High = false, near52Low = false;
  if (q.week52High != null && q.week52Low != null && q.week52High > 0) {
    const pctFrom52H = (q.week52High - q.price) / q.week52High * 100; // 0 = AT 52wk high
    const pctFrom52L = (q.price - q.week52Low)  / q.price       * 100; // 0 = AT 52wk low
    near52High = pctFrom52H <= 5;   // within 5% of 52-week high
    near52Low  = pctFrom52L <= 5;   // within 5% of 52-week low

    if (dir === 'LONG') {
      if (near52High) {
        score += 2; reasons.push('Breakout: near 52-wk high');
      } else if (near52Low) {
        score += 1; reasons.push('Mean-reversion: near 52-wk low');
      }
    } else { // SHORT
      if (near52Low) {
        score += 2; reasons.push('Breakdown: near 52-wk low');
      } else if (near52High) {
        score += 1; reasons.push('Extended: near 52-wk high');
      }
    }
  }

  // ── Pivot Point Support / Resistance ─────────────────────────────────────
  // Classic floor-trader pivot: P = (H + L + C) / 3.
  // R1 = 2P − L  (first resistance), S1 = 2P − H  (first support).
  // A LONG signal near S1 has a natural technical floor under it.
  // A SHORT signal near R1 has a natural ceiling capping further gains.
  // Uses today's intraday H/L/C — zero extra API calls.
  if (q.high > 0 && q.low > 0 && q.price > 0 && q.high !== q.low) {
    const pivot = (q.high + q.low + q.price) / 3;
    const r1    = 2 * pivot - q.low;
    const s1    = 2 * pivot - q.high;
    const distS1Pct = Math.abs(q.price - s1) / q.price * 100;
    const distR1Pct = Math.abs(q.price - r1) / q.price * 100;

    if (dir === 'LONG'  && distS1Pct <= 1.5) { score += 1; reasons.push(`Near pivot support S1 ${q.price > s1 ? 'above' : 'at'} ≈${ s1.toFixed(2)}`); }
    if (dir === 'SHORT' && distR1Pct <= 1.5) { score += 1; reasons.push(`Near pivot resistance R1 ≈${r1.toFixed(2)}`); }
  }

  // ── Stochastic Oscillator — dual-oscillator confirmation ──────────────────
  // %K measures where close sits within the recent high-low range; %D smooths it.
  // Independent of RSI (which measures speed of change), so dual-oversold / dual-overbought
  // confirmation filters out single-oscillator false signals.
  // Scoring is separate from RSI — each oscillator has its own lane.
  let stochConfirms = false;
  if (tech.stochK != null && tech.stochD != null) {
    const k = tech.stochK, d = tech.stochD;
    if (dir === 'LONG') {
      if (k < 20 && d < 20) {
        score += 2; reasons.push(`Stoch oversold K${k.toFixed(0)}/D${d.toFixed(0)}`);
        stochConfirms = true;
      } else if (k < 30 && k > d) {
        score += 1; reasons.push(`Stoch bullish cross K${k.toFixed(0)}`);
        stochConfirms = true;
      } else if (k > 80) {
        score -= 1; // overbought while LONG signal firing = caution
      }
    } else { // SHORT
      if (k > 80 && d > 80) {
        score += 2; reasons.push(`Stoch overbought K${k.toFixed(0)}/D${d.toFixed(0)}`);
        stochConfirms = true;
      } else if (k > 70 && k < d) {
        score += 1; reasons.push(`Stoch bearish cross K${k.toFixed(0)}`);
        stochConfirms = true;
      } else if (k < 20) {
        score -= 1; // oversold while SHORT signal firing = caution
      }
    }
  }

  // ── SuperTrend — trend direction filter ──────────────────────────────────
  // Price above SuperTrend line = institutional bullish bias; below = bearish.
  // With-trend signals have a statistically higher win rate than counter-trend.
  // Counter-trend penalty is -1 (not -2): mean-reversion trades with extreme RSI
  // can still work against the trend, so we discourage but don't block them.
  let supertrendAligned = false;
  if (tech.supertrendUp != null) {
    if (dir === 'LONG') {
      if (tech.supertrendUp)  { score += 2; reasons.push('SuperTrend bullish'); supertrendAligned = true; }
      else                    { score -= 1; } // counter-trend caution
    } else {
      if (!tech.supertrendUp) { score += 2; reasons.push('SuperTrend bearish'); supertrendAligned = true; }
      else                    { score -= 1; } // counter-trend caution
    }
  }

  // ── EMA 50-day — institutional trend structure ────────────────────────────
  // The 50-day EMA is the most watched moving average by institutional desks.
  // Price above EMA50 = accumulation regime; below = distribution regime.
  // Only adds positive points when aligned (no penalty for counter-trend — SuperTrend
  // and RSI already handle that; stacking penalties would over-suppress mean-reversion).
  let ema50Aligned = false;
  if (tech.ema50 != null && q.price > 0) {
    if (dir === 'LONG'  && q.price > tech.ema50) { score += 1; reasons.push(`Above EMA50 $${tech.ema50.toFixed(2)}`); ema50Aligned = true; }
    if (dir === 'SHORT' && q.price < tech.ema50) { score += 1; reasons.push(`Below EMA50 $${tech.ema50.toFixed(2)}`); ema50Aligned = true; }
  }

  // Require both a strong multi-factor setup AND an explicitly set direction.
  // score < 4: not enough confirming indicators (coin-flip territory).
  // dir === 'HOLD': no primary directional indicator fired — no tradeable setup.
  if (dir === 'HOLD') { bump('hold'); return; }
  if (score < 4)     { bump('scoreLow'); return; }

  // Earnings blackout (calendar): cheap O(1) lookup, runs before the news call.
  // Drops the signal if a confirmed earnings date falls inside the blackout window.
  if (earningsBlackoutSet?.has(ticker.toUpperCase())) { bump('earningsBlackout'); return; }

  // ── Pass 2: news sentiment — only for candidates that passed the threshold ──
  let sentiment = { score: 0, label: 'Neutral', count: 0 };
  let earningsBlackout = false;
  if (usePolygon()) {
    try {
      const news = await polygon.getCompanyNews(ticker, 3);
      sentiment = scoreNewsSentiment(news);
      earningsBlackout = hasEarningsContext(news);
    } catch { /* stays neutral */ }
  }

  // Earnings blackout: a recent or imminent earnings event reliably overrides
  // technical setups via overnight gap risk. Drop the signal rather than ship
  // a coin-flip trade.
  if (earningsBlackout) { bump('earningsNews'); return; }

  if (sentiment.label === 'Bullish') {
    if (dir === 'LONG')  { score += 1; reasons.push('News sentiment bullish'); }
    else                 { score -= 1; reasons.push('News bullish (contrarian caution)'); }
  } else if (sentiment.label === 'Bearish') {
    if (dir === 'SHORT') { score += 1; reasons.push('News sentiment bearish'); }
    else                 { score -= 1; reasons.push('News bearish (contrarian caution)'); }
  }

  // ── Confidence ──
  let confidence = 30 + score * 6 + (isRealTech ? 15 : 0);
  if (tech.rsi != null && (tech.rsi < 25 || tech.rsi > 75)) confidence += 5;
  if (q.volume > (q.avgVolume || 0) * 2) confidence += 5;
  if (sentiment.label !== 'Neutral' &&
      ((sentiment.label === 'Bullish' && dir === 'LONG') ||
       (sentiment.label === 'Bearish' && dir === 'SHORT'))) confidence += 8;
  if (tech.adx != null) {
    if      (tech.adx > 40) confidence += 8;
    else if (tech.adx > 25) confidence += 4;
    else if (tech.adx < 20) confidence -= 8;
  }
  // VWAP agreement with direction → institutional confirmation
  if (q.vwap != null && ((dir === 'LONG' && q.price > q.vwap) || (dir === 'SHORT' && q.price < q.vwap))) {
    confidence += 5;
  }
  // Weekly RSI timeframe agreement adds conviction
  if (tech.rsiWeekly != null && ((dir === 'LONG' && tech.rsiWeekly < 60) || (dir === 'SHORT' && tech.rsiWeekly > 40))) {
    confidence += 5;
  }
  // OBV confirmation
  if (tech.obvRising != null && ((dir === 'LONG' && tech.obvRising) || (dir === 'SHORT' && !tech.obvRising))) {
    confidence += 4;
  }
  // Market breadth alignment — macro tailwind adds conviction
  if ((breadthRatio > 0.55 && dir === 'LONG') || (breadthRatio < 0.45 && dir === 'SHORT')) confidence += 4;
  // Relative strength: stock leading the index is institutional-grade momentum
  if (strongRS) confidence += 6;
  // 52-week breakout / breakdown: historically the highest-confidence pattern
  if (near52High && dir === 'LONG')  confidence += 8;
  if (near52Low  && dir === 'SHORT') confidence += 8;
  // Stochastic dual-oscillator confirmation: independent second oscillator agreement
  if (stochConfirms) confidence += 5;
  // SuperTrend alignment: with-trend trades statistically outperform counter-trend
  if (supertrendAligned) confidence += 8;
  // EMA50 institutional trend structure: confirms macro regime supports the trade
  if (ema50Aligned) confidence += 4;
  confidence = Math.min(95, Math.max(20, confidence));

  // Per-regime confidence + score floors. In a stressed VIX regime (≥25),
  // we tighten both — the realised vol environment punishes weak setups
  // hard, so only high-conviction trades clear the bar.
  const gates = regimeGates(vixRegime);
  if (score < gates.minScore)              { bump('scoreLow');      return; }
  if (confidence < gates.minConfidence)    { bump('confidenceLow'); return; }

  // Regime filter: in a bear regime (SPY below EMA50), LONG signals need
  // score ≥ 6 to pass. In a bull regime (SPY above EMA50), the mirror
  // applies to SHORTs — fighting the index is the #1 retail-loss pattern
  // and the asymmetry between LONG and SHORT failure modes was masking
  // bull-tape SHORT losses until now.
  if (!regimeBull && dir === 'LONG'  && score < 6) { bump('regimeFiltered'); return; }
  if ( regimeBull && dir === 'SHORT' && score < 6) { bump('regimeFiltered'); return; }

  // ── LONG counter-trend filter ────────────────────────────────────────────
  // 30-day production data showed LONGs at 25% WR vs SHORTs at 46%. The
  // dominant failure mode was LONGs firing on oversold/mean-reversion setups
  // in stocks that were below their own EMA50 and / or in a bearish SuperTrend
  // regime — classic falling knives. We now require at least one stock-level
  // uptrend confirmation (price > EMA50 AND (SuperTrend bullish OR weekly
  // MACD bullish)) for a LONG. Strong oversold setups (score ≥ 8 normally,
  // ≥ 10 in stressed VIX) can still ship as counter-trend mean-reversion plays.
  if (dir === 'LONG' && score < gates.longCounterTrendScoreMin) {
    const aboveEma50    = tech.ema50 != null && q.price > tech.ema50;
    const supertrendUp  = tech.supertrendUp === true;
    const weeklyMacdUp  = tech.macdWeekly != null && tech.macdSignalWeekly != null && tech.macdWeekly > tech.macdSignalWeekly;
    const stockUptrend  = aboveEma50 && (supertrendUp || weeklyMacdUp);
    if (!stockUptrend) { bump('counterTrendLong'); return; }
  }

  // ── SHORT counter-trend filter ───────────────────────────────────────────
  // Mirror of the LONG filter. Weak SHORTs firing on overbought conditions
  // in stocks that are actually in uptrends (above EMA50, SuperTrend bullish)
  // are "shorting strength" — the same failure mode as catching a knife on
  // the long side. Only deep-overbought setups (score ≥ shortCounterTrend
  // ScoreMin) can ship as counter-trend mean-reversion plays.
  if (dir === 'SHORT' && score < gates.shortCounterTrendScoreMin) {
    const belowEma50      = tech.ema50 != null && q.price < tech.ema50;
    const supertrendDown  = tech.supertrendUp === false;
    const weeklyMacdDown  = tech.macdWeekly != null && tech.macdSignalWeekly != null && tech.macdWeekly < tech.macdSignalWeekly;
    const stockDowntrend  = belowEma50 && (supertrendDown || weeklyMacdDown);
    if (!stockDowntrend) { bump('counterTrendShort'); return; }
  }

  const iv           = tech.impliedVolatility || tech.historicalVolatility || 30;
  const holdingDays  = (tech.rsi != null && (tech.rsi < 35 || tech.rsi > 65)) ? 5 : 3;

  // ATR-adaptive R:R. High-vol names with wide daily ranges over-stretch on a
  // fixed 2:1 — the target never gets hit and the position bleeds out at the
  // swing exit. Tighter R:R on volatile names trades a smaller target for a
  // materially higher hit rate.
  //
  // Continuously interpolated 2.2 → 1.7 as dailyRangePct goes from 1.5% → 6%:
  //   1.5% → 2.20    3.0% → 2.03
  //   2.0% → 2.14    4.0% → 1.92
  //   2.5% → 2.09    6.0% → 1.70   (and clamped at 1.7 for anything wider)
  //
  // This replaces the prior three-bucket step function (2.2/2.0/1.7) which
  // made nearly every top-momentum signal land in the 1.7 bucket — the scanner
  // selects high-vol names by construction, so a coarse step left every R:R
  // pinned to 1.7 in the UI. Continuous interpolation surfaces real
  // differentiation between calmer vs. wilder setups.
  const dailyRangePct = q.high > 0 && q.low > 0 && q.price > 0
    ? Math.max(1.5, Math.min(10.0, ((q.high - q.low) / q.price) * 100))
    : null;
  const rsiExtremity  = tech.rsi != null ? Math.max(0, Math.min(1,
    tech.rsi < 30 ? (30 - tech.rsi) / 20 : tech.rsi > 70 ? (tech.rsi - 70) / 20 : 0)) : 0;
  const baseStopPct   = dailyRangePct != null
    ? +(dailyRangePct * 1.5).toFixed(2)
    : +(2 + rsiExtremity).toFixed(2);
  let rrMultiplier;
  if (dailyRangePct == null) {
    rrMultiplier = 2.0;
  } else {
    // Linear interpolation between (1.5%, 2.20) and (6.0%, 1.70).
    const t = Math.max(0, Math.min(1, (dailyRangePct - 1.5) / (6.0 - 1.5)));
    rrMultiplier = +(2.20 - t * (2.20 - 1.70)).toFixed(2);
  }
  const baseTargetPct = +(baseStopPct * rrMultiplier).toFixed(2);

  const ts = dir === 'LONG'
    ? { targetPrice: +(q.price * (1 + baseTargetPct / 100)).toFixed(2), stopLoss: +(q.price * (1 - baseStopPct / 100)).toFixed(2), targetPct: +baseTargetPct.toFixed(2), stopPct: +(-baseStopPct).toFixed(2), riskReward: +(baseTargetPct / baseStopPct).toFixed(2), expectedMove: +baseTargetPct.toFixed(2) }
    : { targetPrice: +(q.price * (1 - baseTargetPct / 100)).toFixed(2), stopLoss: +(q.price * (1 + baseStopPct / 100)).toFixed(2), targetPct: +(-baseTargetPct).toFixed(2), stopPct: +baseStopPct.toFixed(2), riskReward: +(baseTargetPct / baseStopPct).toFixed(2), expectedMove: +baseTargetPct.toFixed(2) };

  // R:R floor lowered to 1.7 to allow the ATR-adaptive high-vol path through.
  // Breakeven win rate at 1.7:1 is ~37% — comfortably below our SHORT WR and
  // close to the LONG WR target post-uptrend-filter.
  if (!ts.riskReward || ts.riskReward < 1.7) { bump('rrLow'); return; }
  const timeframe = holdingDays <= 1 ? 'Intraday' : `Swing (${holdingDays}d)`;

  const dataSource = `Live (Polygon)`;

  // V2 cluster attribution — run the shared decorrelated scorer in parallel to
  // harvest primary reason cluster + per-cluster contributions. The legacy
  // scoring above still decides direction / confidence so live page behaviour
  // doesn't shift; clusters are pure metadata for the UI badge and outcome
  // attribution when the digest persists them.
  // tech.shortFlow + tech.insiderCluster ride on the technicals batch
  // response (see server/routes/market.js#technicals). Null when the
  // respective daily job hasn't populated this ticker yet — scorer
  // treats null as no contribution.
  const v2 = scoreTickerSignal(q, tech, {
    breadthRatio, indexChangePct,
    shortFlow:      tech?.shortFlow,
    insiderCluster: tech?.insiderCluster,
  });

  // Combined regime label: bear (SPY < EMA50) wins over VIX bands; otherwise
  // surface the VIX classification. Used by the digest persistence and the
  // adaptive-weights walk-forward — and (T3.1) also by the EV gate and
  // confidence calibration so a setup that's poison in this specific regime
  // gets dropped even when its cross-regime average looks OK.
  const regimeLabel = !regimeBull ? 'bear' : (vixRegime || 'normal');

  // ── Hard negative-EV gate ────────────────────────────────────────────────
  // If the (primary cluster × direction × regime) bucket has shown
  // systematically loss-making expectancy over the last 30 days with enough
  // samples to be confident (n ≥ 20), drop the signal entirely. Regime-
  // specific match takes precedence over the cross-regime average.
  if (isNegativeEVBucket(v2.primaryReasonCluster, dir, calibrationMap, regimeLabel)) {
    bump('negativeEV'); return;
  }

  // ── Confidence calibration ───────────────────────────────────────────────
  // Bayesian-shrink the heuristic confidence toward the bucket's observed
  // win rate. Result is what the UI shows; raw confidence stays available
  // for transparency. EV is attached for the downstream ranking step.
  // T3.1: prefer the regime-specific bucket when n ≥ 20.
  const cal = applyCalibration(confidence, v2.primaryReasonCluster, dir, calibrationMap, regimeLabel);
  const calibratedConfidence = cal?.calibratedConfidence ?? confidence;
  const evPct                = cal?.evPct ?? null;
  const trackRecordN         = cal?.sampleN ?? 0;
  const observedWinRate      = cal?.observedWinRate ?? null;
  const atrPct = (q.high > 0 && q.low > 0 && q.price > 0)
    ? +(((q.high - q.low) / q.price) * 100).toFixed(2)
    : null;

  out.push({
    ticker, name, category, categoryColor,
    exchange: 'NYSE',
    direction: dir, score,
    confidence,                  // raw heuristic — kept for transparency / debug
    calibratedConfidence,        // Bayesian-shrunk vs live track record — what the UI should show
    evPct,                       // realised expectancy % per trade for this (cluster × direction)
    trackRecordN,                // sample size behind the calibration (0 if not calibrated)
    observedWinRate,             // bucket WR for the UI footnote
    _confidenceSource: 'heuristic',
    currentPrice: q.price,
    targetPrice: ts.targetPrice, stopLoss: ts.stopLoss,
    targetPct: ts.targetPct, stopPct: ts.stopPct,
    reasons, rsi: tech.rsi, macd: tech.macd,
    volume: q.volume, avgVolume: q.avgVolume, changePct: q.changePct || 0, impliedVol: iv,
    // Underlying's realized/historical volatility — used by the options
    // evaluator's IV-richness check (current IV vs realized). Null on tiers
    // where Polygon doesn't populate it; the check degrades to "no IV flag".
    realizedVol: tech.historicalVolatility ?? null,
    riskReward: ts.riskReward, expectedMove: ts.expectedMove, timeframe,
    newsSentiment: sentiment.label, newsSentimentScore: sentiment.score,
    dataSource, option: null, // enriched after ranking — see generateTradeSignals
    // V2 attribution fields
    primaryReasonCluster: v2.primaryReasonCluster,
    clusterContribs:      v2.clusterContribs,
    regimeLabel,
    atrPct,
  });
  if (stats) stats.shipped = (stats.shipped || 0) + 1;
}

export async function generateTradeSignals(maxSignals = 25, _exchange = 'NYSE', force = false, excludeTickers = [], opts = {}) {
  const excluded = new Set(excludeTickers);
  const isLoadMore = excluded.size > 0;

  // For normal loads (not load-more), serve from cache when valid
  if (!isLoadMore) {
    const cached = signalCache.get('NYSE');
    if (!force && cached && cached.signals.length > 0 && Date.now() - cached.ts < CACHE_TTL.signals) return cached.signals;
  }

  // Fresh stats object for this scan. Each gate inside scoreCandidate increments
  // its own counter; the per-exchange entry is published at the end so the UI
  // can render "scanned 8k → 23 → 3" with a breakdown of where the others went.
  const stats = emptyFilterStats();

  const signals = [];

  // ── NYSE/NASDAQ: full market scan via Polygon grouped daily ─────────────
  //
  // Pass 1 — one API call → all US stocks, filter & rank by momentum
  // Pass 2 — fetch server-computed technicals for top ~50 candidates
  //
  // Scale candidate pool with exclusion count so there are always plenty of
  // fresh candidates after filtering — Load More works indefinitely this way.
  //
  // History: the previous formula was `100 + excluded.size`, which only added
  // ~25 NET fresh candidates per loadMore click after the exclusion filter.
  // The Phase A stack (score gate, sector RS, sector cap of 4) drops ~80% of
  // those, so loadMore routinely shipped zero signals. New formula starts at
  // 200 and adds 4× the exclusion count per click so each loadMore has 150+
  // fresh candidates to score. Worst-case cost: 600 technicals per scan, all
  // cached server-side so repeat clicks within 15min reuse the prior fetches.
  // The opts.expandedPool flag (see retry below) bumps the cap further when a
  // first attempt ships nothing.
  const BASE_POOL = (opts && opts.expandedPool) ? 400 : 200;
  const PER_EXCLUDED = (opts && opts.expandedPool) ? 6 : 4;
  const POOL_CAP    = (opts && opts.expandedPool) ? 800 : 600;
  const SCAN_CANDIDATES = Math.min(POOL_CAP, BASE_POOL + excluded.size * PER_EXCLUDED);
  const MIN_PRICE       = 5;    // filter out penny stocks
  const MIN_VOLUME      = 500_000; // filter out illiquid names

  let candidates = []; // [{ticker, price, changePct, volume, ...}]
  let breadthRatio = 0.5; // default neutral; computed from full universe below

  let indexChangePct = 0; // SPY % change used for relative-strength scoring
  let regimeBull = true;  // default bull; set to false when SPY < EMA50
  // VIX regime — pulled once per scan and used to tighten gates in stress.
  // Failure-safe: a null VIX yields 'normal' (defaults), never breaks the scan.
  const vixLevel  = await polygon.getVixLevel().catch(() => null);
  const vixRegime = vixLevel == null ? 'normal'
                  : vixLevel < 18    ? 'calm'
                  : vixLevel >= 25   ? 'stressed'
                  : 'normal';
  if (vixRegime !== 'normal') {
    console.log(`[Signals] NYSE VIX=${vixLevel?.toFixed?.(1)} → regime '${vixRegime}'`);
  }
  // Publish into stats so the Signals page footer can render the badge
  // and the user can verify which gate floors are actually being applied.
  stats.vixLevel  = vixLevel;
  stats.vixRegime = vixLevel == null ? 'unknown' : vixRegime;
  stats.gates     = regimeGates(vixRegime);

  try {
    const allStocks = await polygon.getMarketScan({ minPrice: MIN_PRICE, minVolume: MIN_VOLUME });
    stats.universeRaw  = allStocks.rawCount ?? allStocks.length;
    stats.universeSize = allStocks.length;

    // Market breadth from the full 8k-stock universe
    if (allStocks.length > 0) {
      breadthRatio = allStocks.filter(s => (s.changePct || 0) > 0).length / allStocks.length;
    }

    // Extract SPY's daily % change as the index reference for RS scoring (zero extra API call)
    const spyBar = allStocks.find(s => s.ticker === 'SPY');
    if (spyBar) indexChangePct = spyBar.changePct || 0;

    // Market regime: SPY price vs 50-day EMA. Bear regime suppresses weak LONG signals.
    // Cached 15 min on the server proxy — adds no meaningful latency after first call.
    try {
      const spyTech = await polygon.getBatchTechnicals(['SPY']);
      if (spyTech?.SPY?.ema50 != null && spyBar?.price != null) {
        regimeBull = spyBar.price >= spyTech.SPY.ema50;
        if (!regimeBull) console.log(`[Signals] NYSE bear regime: SPY ${spyBar.price.toFixed(2)} < EMA50 ${spyTech.SPY.ema50.toFixed(2)}`);
      }
    } catch { /* keep default bull */ }

    // Per-ticker baselines for the whole universe — one extra grouped-daily
    // call per prior day (≤10), all cached 1h. Gives two ranking inputs:
    //   • atrPct    → VOLATILITY-ADJUSTED move ("how many ATRs did it move")
    //   • avgVolume → RELATIVE VOLUME (today ÷ avg), catching unusual
    //                 institutional participation even on quiet-price days.
    // Raw % change alone over-weights always-volatile names and misses
    // meaningful moves on calm names; RVOL adds the "who's actually active"
    // dimension. Failure-safe: an empty map means momentumScore falls back
    // to raw move with no RVOL term (prior behaviour).
    let baselineMap = new Map();
    try {
      baselineMap = await polygon.getUniverseBaselines({ beforeDateStr: allStocks.scanDate || null });
      stats.atrCoverage = baselineMap.size;
      console.log(`[Signals] Universe baselines (ATR + avg-volume) computed for ${baselineMap.size} tickers`);
    } catch (e) {
      console.warn('[Signals] Universe baseline fetch failed — ranking on raw move:', e.message);
    }

    // Rank by the composite of volatility-adjusted move + relative volume,
    // skip already-shown tickers, take top N. Liquidity gate: dollar volume
    // ≥ $25M/day eliminates names where the backtest's close-price
    // assumption isn't a fill we can realistically get. ETP blocklist drops
    // 3x leveraged, inverse, volatility, and single-stock leveraged ETPs.
    const beforeBlock = allStocks.length;
    candidates = allStocks
      .filter(c => (c.price || 0) * (c.volume || 0) >= MIN_DOLLAR_VOLUME_USD)
      .filter(c => !isBlockedEtp(c.ticker))
      .map(c => {
        // Attach ATR% + relative-volume so momentumScore can rank on them and
        // downstream scoring/cards can display them. null when no history.
        const base   = baselineMap.get(c.ticker);
        const atrPct = base?.atrPct ?? null;
        const rvol   = base?.avgVolume > 0 && c.volume > 0
          ? +(c.volume / base.avgVolume).toFixed(2)
          : null;
        return { ...c, atrPct, rvol };
      })
      .sort((a, b) => momentumScore(b) - momentumScore(a))
      .filter(c => !excluded.has(c.ticker))
      .slice(0, SCAN_CANDIDATES);
    const blockedCount = beforeBlock - allStocks.filter(c => !isBlockedEtp(c.ticker)).length;
    if (blockedCount > 0 && stats) stats.leveragedEtpFiltered = blockedCount;

  } catch (e) {
    // Grouped daily unavailable (holiday / weekend edge-case) — fall back to curated list.
    // Record the fact so the UI footer can show "fallback scan" instead of
    // misreporting "scanned 0 stocks → 50 candidates".
    console.warn('[Signals] Market scan failed, falling back to curated universe:', e.message);
    stats.fallback        = true;
    stats.fallbackReason  = e?.message || 'grouped daily unavailable';
    const curated = getAllSecurities();
    stats.universeRaw     = curated.length; // no Polygon scan happened in this fallback
    stats.universeSize    = curated.length;
    const sample = [...curated].filter(s => !excluded.has(s.ticker)).sort(() => Math.random() - 0.5).slice(0, SCAN_CANDIDATES);
    const quotes  = await getBatchQuotes(sample.map(s => s.ticker));
    candidates = sample
      .map(s => quotes[s.ticker])
      .filter(Boolean);
  }

  // Live price refresh: update the top candidates with today's actual prices before scoring.
  // getMarketScan uses yesterday's grouped-daily bars (the only way to scan 8k+ stocks in
  // one call). Stocks can gap dramatically overnight — without this step the signal generator
  // would score a LONG for a stock that has already reversed 50% by market open.
  // getBatchQuotes is cached 15s so this adds no meaningful latency after the first call.
  if (candidates.length > 0) {
    try {
      // Include SPY so we can update indexChangePct to today's intraday move
      const fetchTickers = [...new Set([...candidates.map(c => c.ticker), 'SPY'])];
      const liveQ = await polygon.getBatchQuotes(fetchTickers);

      const spyLive = liveQ['SPY'];
      if (spyLive?.price > 0) {
        // Same fallback logic as per-candidate changePct: intraday when
        // available, day-over-day otherwise.
        indexChangePct = spyLive.open > 0
          ? (spyLive.price - spyLive.open) / spyLive.open * 100
          : (spyLive.changePct ?? indexChangePct);
      }

      candidates = candidates.map(c => {
        const q = liveQ[c.ticker];
        if (!q?.price || q.price <= 0) return c;
        // Prefer today's intraday move (price vs today's session open) — most
        // accurate during market hours. When Polygon hasn't recorded today's
        // session open yet (pre-market, illiquid name, or post-close on a
        // delayed plan), fall back to the snapshot's day-over-day changePct
        // (price vs yesterday's close) so the value is still directional.
        // Never drop to 0 — that silently empties the Dashboard's gainers /
        // losers tiles and hides the move from the scorer.
        const intradayChangePct = q.open > 0
          ? (q.price - q.open) / q.open * 100
          : (q.changePct ?? c.changePct ?? 0);
        return {
          ...c,
          price:     q.price,
          changePct: intradayChangePct,
          open:      q.open   || c.open,
          high:      q.high   || c.high,
          low:       q.low    || c.low,
          vwap:      q.vwap   ?? null, // null = skip VWAP check; never compare today's price to yesterday's VWAP
          volume:    q.volume || c.volume,
        };
      });
    } catch { /* keep grouped-daily prices on API failure */ }
  }

  // Build a lookup from the curated universe for name/category metadata
  const curatedMap = new Map(getAllSecurities().map(s => [s.ticker, s]));

  // Pass 2 — server-computed technicals from a single round trip: server
  // fetches Polygon daily + weekly bars per ticker and returns every
  // indicator + 52-week extremes computed locally.
  const techMap = {};
  const week52Map = {};
  try {
    const tickers   = candidates.map(c => c.ticker);
    const polyBatch = await polygon.getBatchTechnicals(tickers);
    const now = Date.now();
    for (const c of candidates) {
      const p = polyBatch[c.ticker];
      const assembled = p
        ? { ...p, sma20: null, sma50: null, atr: null,
            impliedVolatility: null, historicalVolatility: null,
            _source: 'polygon' }
        : { rsi: null, macd: null, macdSignal: null,
            bollingerUpper: null, bollingerLower: null,
            stochK: null, stochD: null, supertrendUp: null, ema50: null,
            rsiWeekly: null, macdWeekly: null, macdSignalWeekly: null,
            adx: null, obvRising: null, _source: null };
      techMap[c.ticker] = assembled;
      techCache.set(c.ticker, { data: assembled, timestamp: now });
      if (p?.week52High != null && p?.week52Low != null) {
        week52Map[c.ticker] = { week52High: p.week52High, week52Low: p.week52Low };
      }
    }
  } catch {
    await Promise.all(candidates.map(async (c) => {
      try { techMap[c.ticker] = await getTechnicals(c.ticker); } catch { techMap[c.ticker] = {}; }
    }));
  }

  // One earnings-calendar fetch for the whole scan — every candidate then does
  // an O(1) Set lookup. Polygon Benzinga endpoint.
  const earningsBlackoutSet = await getEarningsBlackoutTickers();
  stats.candidatePool = candidates.length;

  // ── Phase A: pull the live track-record calibration once per scan ────────
  // Empty buckets on first deploy / new install — applyCalibration falls
  // back to raw confidence gracefully. Cached server-side so the cost is
  // a single DB query per ~hour.
  // T3.1: regime-aware calibration. The server now returns one row per
  // (cluster, direction, regime) — including the 'all' roll-up — so the map
  // can carry every tier. lookupCalibrationBucket prefers the regime-specific
  // bucket when n ≥ 20, else falls back to 'all'. Backward-compat: if a row
  // lacks a regime field we slot it under 'all' so legacy server responses
  // still resolve.
  const calibrationMap = new Map();
  try {
    const cal = await apiGetCalibration(30);
    for (const b of (cal?.buckets || [])) {
      const regime = b.regime || 'all';
      calibrationMap.set(`${b.cluster}:${b.direction}:${regime}`, b);
    }
  } catch (e) {
    console.warn('[Signals] calibration fetch failed — using raw confidence:', e?.message || e);
  }

  // ── Phase A: sector classifications for sector RS gating ────────────────
  // 24h cache per ticker server-side — first scan on a fresh server pays
  // the lookups, every scan after that is free. Falls back to 'other' for
  // any ticker that errors so the scan never blocks.
  let sectorByTicker = {};
  try {
    sectorByTicker = await polygon.getSectorMap(candidates.map(c => c.ticker));
  } catch (e) {
    console.warn('[Signals] sector fetch failed — sector RS gate disabled:', e?.message || e);
  }
  const sectorRank = computeSectorRSMap(candidates, sectorByTicker);

  // Score each candidate.
  // A throw inside scoreCandidate (e.g. a malformed quote / tech bundle for
  // one ticker) used to reject the whole Promise.all and wipe the batch.
  // Per-ticker catch turns one bad input into one skipped signal, never an
  // empty page.
  await Promise.all(candidates.map(async (c) => {
    const curated  = curatedMap.get(c.ticker);
    const w52      = week52Map[c.ticker] || {};
    try {
      await scoreCandidate({
        ticker:        c.ticker,
        name:          curated?.name          || c.ticker,
        category:      curated?.category      || 'Full Market Scan',
        categoryColor: curated?.categoryColor || '#64748b',
        q:             { ...c, ...w52 },
        tech:          techMap[c.ticker] || {},
        breadthRatio,
        indexChangePct,
        regimeBull,
        vixRegime,
        earningsBlackoutSet,
        calibrationMap,
        stats,
      }, signals);
    } catch (e) {
      console.warn(`[Signals] scoreCandidate failed for ${c.ticker}:`, e.message);
      stats.drops.exception = (stats.drops.exception || 0) + 1;
    }
  }));

  // ── Phase A: sector RS gate ──────────────────────────────────────────────
  // Drop LONGs that fired in lagging sectors and SHORTs that fired in leading
  // sectors. A PM doesn't long financials when energy is leading the tape;
  // this enforces the same discipline. Tag dropped signals in stats so the
  // user can see the filter doing its work.
  const sectorFiltered = [];
  for (const s of signals) {
    const sector = sectorByTicker[s.ticker] || 'other';
    if (sectorRSAllows(s.direction, sector, sectorRank)) {
      sectorFiltered.push({ ...s, sector });
    } else {
      stats.drops.sectorFiltered = (stats.drops.sectorFiltered || 0) + 1;
      // The signal was already counted in stats.shipped during scoring;
      // back it out so shipped reflects what actually leaves the funnel.
      stats.shipped = Math.max(0, (stats.shipped || 0) - 1);
    }
  }

  // ── Phase A: EV-based ranking with sector diversification ────────────────
  // Primary sort: EV (expectancy %) when calibration data exists for the
  // bucket. Fallback: calibrated confidence (which itself falls back to raw
  // confidence when there's no track-record data). Score is only a deep
  // tiebreaker for signals where neither EV nor calibration applies.
  sectorFiltered.sort((a, b) => {
    const aEv = a.evPct, bEv = b.evPct;
    if (aEv != null && bEv != null && Math.abs(aEv - bEv) > 0.01) return bEv - aEv;
    if (aEv != null && bEv == null) return -1;
    if (bEv != null && aEv == null) return 1;
    const aC = a.calibratedConfidence ?? a.confidence;
    const bC = b.calibratedConfidence ?? b.confidence;
    if (Math.abs(aC - bC) > 0.5) return bC - aC;
    return (b.score || 0) - (a.score || 0);
  });

  // Sector diversification cap — no more than 4 signals from the same sector
  // in the top N. Real desks don't ship 8 semis at once; idiosyncratic risk
  // dominates correlated bets. Cap is loose enough to let the strongest
  // sector lead with four picks, strict enough to force breadth across the
  // grid. Dropped signals decrement shipped so the per-scan stats invariant
  // holds.
  const { kept: diversified, dropped: capDropped } = applySectorCap(sectorFiltered, sectorByTicker, 4);
  if (capDropped.length > 0) {
    stats.drops.sectorCapped = (stats.drops.sectorCapped || 0) + capDropped.length;
    stats.shipped = Math.max(0, (stats.shipped || 0) - capDropped.length);
  }
  const sorted = diversified.slice(0, maxSignals);

  // ── Options enrichment — fetch for a wider pool so that after strict quality
  // filters (delta ≥ 0.15, strike within 20%) enough signals still get options.
  // We fetch for up to 2× maxSignals candidates (capped at all sorted signals),
  // then keep only the top maxSignals for display regardless.
  if (usePolygon()) {
    const enrichPool = diversified.slice(0, maxSignals * 2);
    let enrichErrors = 0;
    let optionsDropped = 0;
    await Promise.all(enrichPool.map(async (sig) => {
      try {
        const realOpt = await polygon.findBestOption(sig.ticker, sig.direction, sig.currentPrice);
        if (!realOpt) return;
        const built = buildRealOption(realOpt, sig.direction, sig.impliedVol || 30);
        // Options-native evaluation: compute POP + delta-theta EV + IV
        // richness, and GATE. We no longer attach an option just because a
        // near-ATM contract exists — only when it's not expected to lose
        // (verdict !== 'unfavorable'). This kills the old behaviour of
        // showing the stock's win-rate on a theta-bleeding option.
        const evalResult = evaluateOptionSignal(built, {
          spotPrice:     sig.currentPrice,
          holdingDays:   sig.holdingDays || sig.days || 5,
          realizedVolPct: sig.realizedVol ?? null,   // underlying's historical vol; null → no IV-richness flag
          // Credit the directional edge modestly: 40% of the forecast target
          // move, signed by direction. Not full target (would overstate).
          driftPct:      sig.direction === 'SHORT'
            ? -Math.abs(Number(sig.targetPct) || 0) / 100 * 0.4
            :  Math.abs(Number(sig.targetPct) || 0) / 100 * 0.4,
        });
        if (evalResult && evalResult.verdict === 'unfavorable') {
          optionsDropped++;
          return;   // expected to lose — don't attach it
        }
        built.evaluation = evalResult || null;
        sig.option = built;
      } catch (e) {
        enrichErrors++;
        if (enrichErrors <= 2) console.warn(`[Signals] Options fetch failed for ${sig.ticker}:`, e?.message || e);
      }
    }));
    const enriched = enrichPool.filter(s => s.option).length;
    console.info(`[Signals] Options enrichment: ${enriched}/${enrichPool.length} got a favorable option (${optionsDropped} dropped as unfavorable, ${enrichErrors} errors)`);
  }

  // Only update the primary cache for normal loads, not load-more calls
  // (load-more results are a different slice and shouldn't overwrite the base cache)
  if (!isLoadMore && sorted.length > 0) {
    signalCache.set('NYSE', { signals: sorted, ts: Date.now() });
  }

  // Publish the per-scan filter stats so the UI can explain low counts.
  // Sector RS snapshot is exposed so the Signals page footer can show "tech
  // leading, energy lagging" alongside the VIX badge.
  stats.timestamp = Date.now();
  stats.sectorRank = Object.fromEntries(sectorRank);
  stats.calibrationBuckets = calibrationMap.size;
  lastFilterStats = stats;

  // loadMore retry: if a first pass with the standard pool shipped zero
  // signals, the next-best candidates were probably weaker movers that lost
  // to the score/confidence/sector gates. Expand the pool once and try
  // again so users aren't told "no more signals" prematurely. Only triggers
  // for loadMore (excluded.size > 0) and only once — the `opts.expandedPool`
  // flag prevents infinite recursion when even the expanded scan ships 0.
  if (isLoadMore && sorted.length === 0 && !(opts && opts.expandedPool)) {
    const cause = describeZeroShip(stats);
    console.log(`[Signals] loadMore shipped 0 with pool=${SCAN_CANDIDATES} (${cause}) — retrying with expanded pool…`);
    return generateTradeSignals(maxSignals, _exchange, force, excludeTickers, { expandedPool: true });
  }
  if (isLoadMore && sorted.length === 0) {
    console.log(`[Signals] loadMore shipped 0 even with expanded pool — no more gate-passing candidates beyond rank ${SCAN_CANDIDATES}.`);
  }

  return sorted;
}

// Best-effort "what filter killed it" string for the loadMore retry log.
function describeZeroShip(stats) {
  if (!stats || !stats.drops) return 'unknown';
  const top = Object.entries(stats.drops)
    .filter(([, n]) => n > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([k, n]) => `${k}:${n}`);
  return top.length ? top.join(', ') : 'no scored candidates';
}

// ============================================================================
// MARKET OVERVIEW
// ============================================================================
export async function getMarketOverview() {
  const indexConfig = {
    tickers: ['SPY', 'QQQ', 'IWM', 'DIA'],
    names: { SPY: 'S&P 500', QQQ: 'NASDAQ 100', IWM: 'Russell 2000', DIA: 'Dow Jones' },
  };

  // Fan out everything in parallel — VIX, grouped-daily, indices, news.
  // getMarketScan is cached 15min via polygonFetch, so a Dashboard call
  // right after a Signals scan hits the cache for free.
  //
  // VIX comes from our dedicated server endpoint (apiGetVix) rather than
  // proxying I:VIX through the generic polygon proxy — the index ticker
  // has its own entitlement check that the proxy doesn't surface cleanly.
  const [quotes, vixResp, scan, news] = await Promise.all([
    getBatchQuotes(indexConfig.tickers),
    apiGetVix().catch(() => ({ vix: null })),
    polygon.getMarketScan().catch(() => null),
    getMarketNews().catch(() => []),
  ]);
  const vix = vixResp?.vix ?? null;

  const indices = indexConfig.tickers.map(t => ({
    name: indexConfig.names[t], ticker: t,
    price: quotes[t]?.price || 0,
    change: quotes[t]?.change || 0,
    changePct: quotes[t]?.changePct || 0,
  })).filter(i => i.price > 0);

  const spyQ = quotes['SPY'];
  const spyChange = spyQ?.changePct || 0;

  // Breadth — count advancers / decliners across every liquid US stock
  // in today's grouped-daily snapshot. Far more honest than computing
  // breadth from our 50-signal slice would be.
  let advancers = null, decliners = null;
  if (Array.isArray(scan) && scan.length > 0) {
    advancers = scan.filter(s => (s.changePct || 0) > 0).length;
    decliners = scan.filter(s => (s.changePct || 0) < 0).length;
  }

  // Fear / Greed proxy — three-factor composite centred at 50.
  //
  //   VIX:     <15 → strong greed (+25); >30 → strong fear (−25); linear.
  //   Breadth: (adv−dec)/total → ±15.
  //   SPY:     today's % change clamped to ±10.
  //
  // Not the CNN F/G index (which uses 7 inputs we don't fetch), but a
  // reasonable directional read using only data we already have.
  let fearGreedIndex = null;
  if (vix != null || advancers != null) {
    let score = 50;
    if (vix != null) {
      // 10 → +30, 20 → +10, 25 → 0, 30 → −10, 40 → −30. Clamp ±30.
      const vixContrib = Math.max(-30, Math.min(30, (22.5 - vix) * 2));
      score += vixContrib;
    }
    if (advancers != null && decliners != null) {
      const total = advancers + decliners;
      if (total > 0) {
        const breadthContrib = ((advancers - decliners) / total) * 15;
        score += Math.max(-15, Math.min(15, breadthContrib));
      }
    }
    score += Math.max(-10, Math.min(10, spyChange * 5));
    fearGreedIndex = Math.round(Math.max(0, Math.min(100, score)));
  }

  return {
    indices, news,
    vix,
    advancers, decliners,
    sentiment: spyChange > 0.5 ? 'Bullish' : spyChange < -0.5 ? 'Bearish' : 'Neutral',
    fearGreedIndex,
    dataSource: indices.length > 0 ? 'live' : 'unavailable',
  };
}

export function isLiveData() { return hasApiKeys(); }
export function getDataSource() { return 'Polygon.io'; }
